/**
 * UploadFlowSteps — the reusable 5-step upload UI
 * (QR → photo → thinking → reveal → recs).
 *
 * Step 5 (`recs`) was added in Phase 2.9.1 to show the user the *current*
 * recommendation set while still inside the modal, before the modal closes.
 * It reads from the same `useRecommendations()` hook as the bottom-of-scene
 * `RecommendationStack`, so BE-driven recs and the FE-derived fallback both
 * surface there. The step's "Hotovo" CTA is what actually fires the parent's
 * `onConfirm` (i.e. the contract-confirm mutation in the modal path, or the
 * navigation to `/contract/:id` in the page path). The "Zpět" button drops
 * back to step 4.
 *
 * Extracted from `pages/UploadFlow.tsx` in Phase 2.8 so the same step machinery
 * renders in two surfaces:
 *
 *   1. `/upload?zone=X` page  → `pages/UploadFlow.tsx` mounts these steps inside
 *      the full-page `bg-pop-night` chrome (used for direct URL access).
 *   2. `<UploadModal>`         → mounts these steps inside a centred pixel-card
 *      with a translucent backdrop, so the home scene stays visible behind.
 *
 * The parent owns the navigation decision on confirm: the page navigates to
 * `/contract/:id`, the modal closes itself + transitions the scene. The
 * `onConfirm` callback receives the full BE envelope (`{ contract, coverages,
 * ocr }`) — the page reads `contract.id` to route, the modal reads it to
 * fire `confirmContract.mutate(contract.id)`. The `ocr` payload drives the
 * reveal preview.
 *
 * Phase 2.9 — real-BE wiring:
 *   `useUploadContract()` now hits `POST /api/contracts/upload` and the
 *   response shape is `ContractUploadResponse` (not the bare `OcrResult` we
 *   had under MSW). The reveal step still renders OCR fields, just via
 *   `result.ocr.*`.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { AlertTriangle, Camera, FileText, Loader2, RefreshCw, Smartphone, Sparkles } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import {
  useCreateUploadSession,
  useRecommendations,
  useUploadContract,
  useUploadTokenStatus,
} from '@/hooks/queries'
import { apiGet, ApiError } from '@/lib/api'
import { AREA_ICON, pictogramFor } from '@/components/pictograms'
import {
  ContractsResponseSchema,
  type AreaCode,
  type Contract,
  type ContractUploadResponse,
  type OcrResult,
  type Recommendation,
} from '@shared/schemas'

export type UploadZone = 'house' | 'vehicle' | 'travel' | 'life' | 'pet'

// Zone → allowed OCR area codes for the soft mismatch warning on the reveal
// step. Broader than `ZONE_AREAS` in scene/zoneState.ts (which only lists the
// areas that drive scene-tile coverage state). Here we want every sub-area
// the user could plausibly add via that tile so we don't false-positive on
// e.g. property_glass uploaded from the house tile.
const ZONE_UPLOAD_AREAS: Record<UploadZone, readonly AreaCode[]> = {
  house: ['property_building', 'property_home', 'property_glass', 'property_theft'],
  vehicle: ['vehicle_mtpl', 'vehicle_casco', 'vehicle_glass', 'vehicle_assistance', 'vehicle_gap'],
  travel: ['travel', 'travel_cancellation', 'travel_luggage', 'travel_sport'],
  life: ['life', 'accident', 'disability_invalidity', 'critical_illness', 'mortgage_protection'],
  pet: ['pet', 'pet_liability'],
}

const ZONE_LABEL: Record<UploadZone, string> = {
  house: 'dům',
  vehicle: 'auto',
  travel: 'cesty',
  life: 'život',
  pet: 'mazlíčka',
}

export type UploadFlowStepsProps = {
  zone: UploadZone
  /**
   * Called when the user clicks the "Pokračovat" / Confirm button on step 4.
   * Receives the full BE envelope — the page form uses `result.contract.id`
   * to navigate to `/contract/:id`, the modal form uses it to fire the
   * confirm mutation.
   */
  onConfirm: (result: ContractUploadResponse) => void
  /** Called when the user wants to bail. Used by the modal backdrop / Zrušit btn. */
  onCancel: () => void
  /**
   * Optional CTA label override for the final reveal-step button. The page
   * variant ships "Pokračovat" (because it navigates to ContractDetail); the
   * modal variant ships "Potvrdit" (because the modal IS the confirm).
   */
  confirmLabel?: string
}

type Step = 'qr' | 'photo' | 'thinking' | 'reveal' | 'recs'

const stepVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
}

const stepTransition = { duration: 0.24, ease: [0, 0, 0, 1] as const }

const STEP_LABEL: Record<Step, string> = {
  qr: 'Krok 1 / Naskenuj kód',
  photo: 'Krok 2 / Vyfoť smlouvu',
  thinking: 'Krok 3 / Čtu smlouvu',
  reveal: 'Krok 4 / Náhled',
  recs: 'Krok 5 / Doporučení',
}

export function UploadFlowSteps({
  zone,
  onConfirm,
  onCancel,
  confirmLabel = 'Pokračovat',
}: UploadFlowStepsProps): JSX.Element {
  const [step, setStep] = useState<Step>('qr')
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [result, setResult] = useState<ContractUploadResponse | null>(null)
  const uploadContract = useUploadContract()

  const startUpload = useCallback(
    (picked: File) => {
      setFile(picked)
      setPreviewUrl(URL.createObjectURL(picked))
      setStep('thinking')
      uploadContract.mutate(picked, {
        onSuccess: (uploaded) => {
          setResult(uploaded)
          setStep('reveal')
        },
        onError: () => {
          setStep('photo')
        },
      })
    },
    [uploadContract],
  )

  // QR handoff: the phone uploaded via token, so the desktop only sees a bare
  // Contract (no OCR payload). Synthesize a ContractUploadResponse so the same
  // `reveal` step + `onConfirm(result)` contract holds — parents only read
  // `result.contract.id`, and the reveal preview reads fields present on both.
  const onPhoneDetected = useCallback((contract: Contract) => {
    setResult(syntheticUploadResponse(contract))
    setStep('reveal')
  }, [])

  return (
    <div className="w-full max-w-md" data-testid="upload-flow-steps" data-zone={zone}>
      <AnimatePresence mode="wait">
        {step === 'qr' && (
          <StepFrame key="qr" step={step}>
            <QrStep
              onPhoneDetected={onPhoneDetected}
              onUseThisDevice={() => setStep('photo')}
              onCancel={onCancel}
            />
          </StepFrame>
        )}

        {step === 'photo' && (
          <StepFrame key="photo" step={step}>
            <h1 className="text-xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
              Vyfoť smlouvu
            </h1>
            <DropZone
              onFile={startUpload}
              previewUrl={previewUrl}
              fileName={file?.name}
            />
            <button onClick={onCancel} className="pixel-btn-secondary text-xs">
              Zrušit
            </button>
          </StepFrame>
        )}

        {step === 'thinking' && (
          <StepFrame key="thinking" step={step}>
            <ThinkingLoader />
          </StepFrame>
        )}

        {step === 'reveal' && result && (
          <StepFrame key="reveal" step={step}>
            <OcrPreview ocr={result.ocr} zone={zone} />
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setStep('recs')}
                className="pixel-btn"
                data-testid="upload-confirm-btn"
              >
                {confirmLabel}
              </button>
              <button onClick={onCancel} className="pixel-btn-secondary text-xs">
                Zrušit
              </button>
            </div>
          </StepFrame>
        )}

        {step === 'recs' && result && (
          <StepFrame key="recs" step={step}>
            <RecsSummary
              onDone={() => onConfirm(result)}
              onBack={() => setStep('reveal')}
            />
          </StepFrame>
        )}
      </AnimatePresence>
    </div>
  )
}

export default UploadFlowSteps

// ─── Step frame ────────────────────────────────────────────────────────────
function StepFrame({
  step,
  children,
}: {
  step: Step
  children: ReactNode
}) {
  return (
    <motion.div
      variants={stepVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={stepTransition}
      className="pixel-card overflow-hidden"
      data-step={step}
    >
      <div className="bg-pop-silhouette text-pop-cream font-mono uppercase text-xs px-3 py-1 tracking-wider border-b-[3px] border-pop-silhouette">
        {STEP_LABEL[step]}
      </div>
      <div className="flex flex-col items-center gap-5 p-6">{children}</div>
    </motion.div>
  )
}

// ─── QR step (real scannable code + phone-upload handoff) ───────────────────
/**
 * Issues a real upload-session token, renders a scannable QR pointing at the
 * `/upload/phone?token=…` page, and polls `GET /api/contracts` to detect when
 * the phone has uploaded. When a new contract appears it hands the bare
 * Contract up to `onPhoneDetected`.
 *
 * Fallbacks:
 *   - 401 (desktop not logged in) → no token can be issued; show a prompt and
 *     route the user to the on-device camera (which keeps the anonymous
 *     first-upload story alive via `useUploadContract`).
 *   - "Vyfotit na tomto zařízení" is always offered as a no-phone escape hatch.
 */
function QrStep({
  onPhoneDetected,
  onUseThisDevice,
  onCancel,
}: {
  onPhoneDetected: (contract: Contract) => void
  onUseThisDevice: () => void
  onCancel: () => void
}) {
  const createSession = useCreateUploadSession()
  const mutate = createSession.mutate
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

  // Issue a token on mount.
  useEffect(() => {
    mutate()
  }, [mutate])

  const session = createSession.data
  const expiresAt = session?.expires_at
  const isExpired = secondsLeft !== null && secondsLeft <= 0

  // Countdown ticker driven by the token's expires_at.
  useEffect(() => {
    if (!expiresAt) return
    const tick = () => {
      const diff = Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)
      setSecondsLeft(diff)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [expiresAt])

  // Poll contracts and token status while a live token is shown.
  const pollEnabled = Boolean(session) && !isExpired
  const baselineRef = useRef<Set<string> | null>(null)
  const newContractsRef = useRef<Contract[]>([])
  const handedOffRef = useRef(false)

  const contractsPoll = useQuery({
    queryKey: ['contracts', 'qr-poll'],
    queryFn: () => apiGet<Contract[]>('/api/contracts', ContractsResponseSchema),
    refetchInterval: pollEnabled ? 2500 : false,
    enabled: pollEnabled,
    gcTime: 0,
  })

  const tokenStatus = useUploadTokenStatus(
    session?.upload_token ?? '',
    pollEnabled,
  )
  const tokenFinished = tokenStatus.data?.finished === true

  // Reset on fresh token.
  useEffect(() => {
    baselineRef.current = null
    newContractsRef.current = []
    handedOffRef.current = false
  }, [session?.upload_token])

  // Accumulate new contracts as they arrive; hand off when phone signals done.
  useEffect(() => {
    const data = contractsPoll.data
    if (!data) return
    if (baselineRef.current === null) {
      baselineRef.current = new Set(data.map((c) => c.id))
      return
    }
    const newOnes = data.filter((c) => !baselineRef.current!.has(c.id))
    if (newOnes.length > 0) newContractsRef.current = newOnes
    if (!handedOffRef.current && tokenFinished && newContractsRef.current.length > 0) {
      handedOffRef.current = true
      onPhoneDetected(newContractsRef.current[0]!)
    }
  }, [contractsPoll.data, tokenFinished, onPhoneDetected])

  // Also check hand-off when token status arrives (contracts may already be loaded).
  useEffect(() => {
    if (!handedOffRef.current && tokenFinished && newContractsRef.current.length > 0) {
      handedOffRef.current = true
      onPhoneDetected(newContractsRef.current[0]!)
    }
  }, [tokenFinished, onPhoneDetected])

  const notLoggedIn =
    createSession.isError &&
    createSession.error instanceof ApiError &&
    createSession.error.status === 401

  const phoneUrl = session
    ? `${window.location.origin}/upload/phone?token=${encodeURIComponent(session.upload_token)}`
    : null

  return (
    <>
      <h1 className="text-xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
        Naskenuj smlouvu telefonem
      </h1>

      {notLoggedIn ? (
        <NotLoggedInFallback onUseThisDevice={onUseThisDevice} />
      ) : (
        <>
          <p className="text-sm text-pop-shadow text-center max-w-xs">
            Namiř telefon na QR kód, vyfoť smlouvu a objeví se tu sama.
          </p>

          <div className="relative w-[208px] h-[208px] border-[3px] border-pop-silhouette bg-pop-cream p-3 flex items-center justify-center">
            {phoneUrl && !isExpired ? (
              <QRCodeSVG
                value={phoneUrl}
                size={184}
                bgColor="#F5E6C8"
                fgColor="#1F1410"
                level="M"
              />
            ) : isExpired ? (
              <div className="flex flex-col items-center gap-2 text-center text-pop-shadow">
                <RefreshCw className="h-8 w-8" strokeWidth={2.5} />
                <span className="text-xs font-mono uppercase">Kód vypršel</span>
              </div>
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-pop-fire" strokeWidth={3} />
            )}
          </div>

          {/* Countdown + waiting indicator */}
          <div className="flex items-center gap-2 text-xs font-mono uppercase text-pop-shadow min-h-[1.25rem]">
            {isExpired ? (
              <button
                onClick={() => mutate()}
                className="pixel-btn-secondary flex items-center gap-1 text-xs"
              >
                <RefreshCw className="h-3.5 w-3.5" strokeWidth={3} />
                Obnovit kód
              </button>
            ) : secondsLeft !== null ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-pop-fire" strokeWidth={3} />
                <span>Čekám na telefon · {formatCountdown(secondsLeft)}</span>
              </>
            ) : null}
          </div>

          <button
            onClick={onUseThisDevice}
            className="pixel-btn-secondary flex items-center gap-2 text-xs"
          >
            <Smartphone className="h-3.5 w-3.5" strokeWidth={2.5} />
            Vyfotit na tomto zařízení
          </button>
        </>
      )}

      <button onClick={onCancel} className="pixel-btn-secondary text-xs">
        Zrušit
      </button>
    </>
  )
}

function NotLoggedInFallback({ onUseThisDevice }: { onUseThisDevice: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 text-center max-w-xs">
      <p className="text-sm text-pop-shadow">
        Přenos z telefonu je dostupný po přihlášení. Teď můžeš smlouvu vyfotit
        rovnou na tomto zařízení.
      </p>
      <button
        onClick={onUseThisDevice}
        className="pixel-btn flex items-center gap-2"
      >
        <Camera className="h-5 w-5" strokeWidth={2.5} />
        Vyfotit smlouvu
      </button>
    </div>
  )
}

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const rem = s % 60
  return `${m}:${rem.toString().padStart(2, '0')}`
}

// Build a ContractUploadResponse from a bare Contract detected via polling.
// `contract_number` is unknown from the list endpoint — the reveal preview
// shows a placeholder; the real value lives on the contract detail.
function syntheticUploadResponse(contract: Contract): ContractUploadResponse {
  return {
    contract,
    coverages: [],
    ocr: {
      contract_id: contract.id,
      insurer: contract.insurer,
      type: contract.type,
      holder_member_id: contract.holder_member_id,
      insured_person_ids: contract.insured_member_ids,
      coverages: [],
      premium_czk: contract.premium_czk,
      valid_from: contract.valid_from,
      valid_to: contract.valid_to,
      contract_number: '—',
      status: contract.status,
    },
  }
}

// ─── Drag-and-drop / file input ────────────────────────────────────────────
function DropZone({
  onFile,
  previewUrl,
  fileName,
}: {
  onFile: (file: File) => void
  previewUrl: string | null
  fileName?: string
}) {
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onPickFromInput = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    if (picked) onFile(picked)
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const picked = e.dataTransfer.files?.[0]
    if (picked) onFile(picked)
  }

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`pixel-input border-dashed px-6 py-10 cursor-pointer transition-colors duration-75 ease-linear ${
          isDragOver ? 'bg-pop-sand-light' : 'hover:bg-pop-sand-light'
        }`}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
            <Camera className="h-8 w-8 text-pop-silhouette" strokeWidth={2.5} />
          </div>
          <div className="text-sm font-semibold uppercase tracking-wide text-pop-silhouette">
            Vyfoť smlouvu
          </div>
          <div className="text-xs text-pop-shadow">nebo sem přetáhni fotku / PDF</div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={onPickFromInput}
        />
      </div>

      <button onClick={() => inputRef.current?.click()} className="pixel-btn">
        Vybrat soubor
      </button>

      {previewUrl && (
        <div className="pixel-card flex items-center gap-3 text-xs px-2 py-2">
          <img
            src={previewUrl}
            alt="Náhled smlouvy"
            className="w-12 h-12 object-cover border-[3px] border-pop-silhouette"
          />
          <div className="truncate max-w-[12rem] font-mono text-pop-silhouette">
            {fileName ?? 'photo.jpg'}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── "Čtu smlouvu…" loader ────────────────────────────────────────────────
function ThinkingLoader() {
  return (
    <div className="flex flex-col items-center gap-5 w-full">
      <div className="bg-pop-torch border-[3px] border-pop-silhouette p-4">
        <FileText className="h-10 w-10 text-pop-silhouette" strokeWidth={2.5} />
      </div>

      <div className="flex items-center gap-2 text-pop-silhouette font-mono uppercase text-sm">
        <Loader2 className="h-4 w-4 animate-spin text-pop-fire" strokeWidth={3} />
        <span>Čtu smlouvu…</span>
      </div>

      <div className="pixel-progress w-64">
        <motion.div
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: 1.4, ease: 'linear' }}
        />
      </div>
      <p className="text-xs text-pop-shadow max-w-xs text-center">
        Vytahujeme z fotky pojistitele, typ smlouvy, platnost a pojištěné osoby.
      </p>
    </div>
  )
}

// ─── OCR preview ──────────────────────────────────────────────────────────
function OcrPreview({ ocr, zone }: { ocr: OcrResult; zone: UploadZone }) {
  const Icon = AREA_ICON[ocr.type] ?? FileText
  const mismatch = !ZONE_UPLOAD_AREAS[zone].includes(ocr.type)
  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
          <Icon className="h-6 w-6 text-pop-silhouette" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-pop-shadow">
            Nová smlouva
          </div>
          <div className="text-base font-semibold uppercase tracking-wide text-pop-silhouette truncate">
            {ocr.insurer}
          </div>
        </div>
      </div>
      {mismatch && <ZoneMismatchBanner zone={zone} ocrType={ocr.type} />}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Field label="Typ" value={czechAreaLabel(ocr.type)} />
        <Field label="Pojistné" value={`${ocr.premium_czk.toLocaleString('cs-CZ')} Kč / rok`} mono />
        <Field label="Platí od" value={ocr.valid_from} mono />
        <Field label="Platí do" value={ocr.valid_to} mono />
        <Field label="Číslo smlouvy" value={ocr.contract_number} mono />
        <Field label="Pojištěných" value={`${ocr.insured_person_ids.length}`} mono />
      </dl>
    </div>
  )
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col border-b-[2px] border-pop-sand-dark pb-1.5 last:border-b-0">
      <dt className="text-[10px] font-mono uppercase tracking-wider text-pop-sand-dark">{label}</dt>
      <dd
        className={`truncate text-pop-silhouette font-medium ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </dd>
    </div>
  )
}

// Soft warning when the OCR'd contract type doesn't sit inside the zone the
// user clicked. Non-blocking — the BE stores the contract by OCR type
// regardless, and the warning just makes sure the user knows it'll land
// elsewhere in the atlas. See issue #60.
function ZoneMismatchBanner({ zone, ocrType }: { zone: UploadZone; ocrType: AreaCode }) {
  return (
    <div
      role="alert"
      data-testid="upload-zone-mismatch"
      data-zone={zone}
      data-ocr-type={ocrType}
      className="pixel-card border-l-[10px] border-l-pop-caution bg-pop-sand-light flex items-start gap-3 p-3"
    >
      <div className="bg-pop-caution border-[3px] border-pop-silhouette p-1.5 shrink-0">
        <AlertTriangle className="h-4 w-4 text-pop-silhouette" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex flex-col gap-1">
        <div className="text-[10px] font-mono uppercase tracking-wider text-pop-silhouette">
          Vypadá to jako jiný typ smlouvy
        </div>
        <p className="text-[11px] leading-snug text-pop-shadow">
          Klikl jsi na <span className="font-semibold">{ZONE_LABEL[zone]}</span>, ale tahle smlouva
          vypadá jako <span className="font-semibold">{czechAreaLabel(ocrType)}</span>. Můžeš
          pokračovat — uložíme ji pod správnou kategorii.
        </p>
      </div>
    </div>
  )
}

function czechAreaLabel(code: string): string {
  switch (code) {
    case 'liability_personal':
      return 'Odpovědnost za škodu'
    case 'vehicle_mtpl':
      return 'Povinné ručení'
    case 'property_building':
      return 'Pojištění nemovitosti'
    case 'property_home':
      return 'Pojištění domácnosti'
    case 'travel':
      return 'Cestovní pojištění'
    case 'life':
      return 'Životní pojištění'
    default:
      return code
  }
}

// ─── Step 5 — Recommendations summary ─────────────────────────────────────
/**
 * Step 5: a brief, narrow-friendly preview of the current recommendation set.
 *
 * Uses the same `useRecommendations()` hook as `RecommendationStack`, so the
 * cards rendered here are exactly what the user will see at the bottom of the
 * scene once the modal closes (BE first, FE-derived fallback when the BE rule
 * engine is empty — see `frontend/src/lib/derived-recommendations.ts`).
 *
 * Layout is intentionally a **slim inline list** (not the full
 * `<RecommendationCard>` from the scene) because the modal card is 28rem
 * wide; the bottom-scene cards are 17.5rem each + horizontal scroll, which
 * doesn't fit well inside a vertical modal. The inline rows here keep the
 * severity stripe + icon affordance.
 */
function RecsSummary({
  onDone,
  onBack,
}: {
  onDone: () => void
  onBack: () => void
}) {
  const { data: recs = [], isPending } = useRecommendations()
  const top = recs.slice(0, 3)

  return (
    <div className="w-full flex flex-col gap-4" data-testid="upload-recs-summary">
      <div className="flex items-center gap-3">
        <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
          <Sparkles className="h-6 w-6 text-pop-silhouette" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-pop-shadow">
            Doporučení
          </div>
          <div className="text-base font-semibold uppercase tracking-wide text-pop-silhouette truncate">
            {top.length > 0 ? 'Po této smlouvě stále chybí' : 'Vše pokryto'}
          </div>
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 text-pop-silhouette font-mono uppercase text-xs">
          <Loader2 className="h-4 w-4 animate-spin text-pop-fire" strokeWidth={3} />
          <span>Načítám doporučení…</span>
        </div>
      ) : top.length === 0 ? (
        <p className="text-sm text-pop-shadow">
          Jsi pokrytý — nic dalšího ti zatím nedoporučujeme.
        </p>
      ) : (
        <>
          <p className="text-sm text-pop-shadow">
            Po této smlouvě ti stále doporučujeme:
          </p>
          <ul
            className="flex flex-col gap-2"
            data-testid="upload-recs-list"
          >
            {top.map((rec) => (
              <RecRow key={rec.id} rec={rec} />
            ))}
          </ul>
        </>
      )}

      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={onDone}
          className="pixel-btn"
          data-testid="upload-recs-done-btn"
        >
          Hotovo
        </button>
        <button
          type="button"
          onClick={onBack}
          className="pixel-btn-secondary text-xs"
          data-testid="upload-recs-back-btn"
        >
          Zpět
        </button>
      </div>
    </div>
  )
}

/** Slim inline row for one recommendation — modal-friendly variant. */
function RecRow({ rec }: { rec: Recommendation }) {
  const Icon = rec.area ? pictogramFor(rec.area) : Sparkles
  const edge =
    rec.severity === 'high'
      ? 'border-l-pop-severe'
      : rec.severity === 'medium'
        ? 'border-l-pop-caution'
        : 'border-l-pop-calm'
  return (
    <li
      data-testid="upload-recs-row"
      data-rule-id={rec.rule_id}
      className={`pixel-card flex items-center gap-3 p-2 border-l-[10px] ${edge}`}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center border-[3px] border-pop-silhouette bg-pop-sand-light">
        <Icon className="h-4 w-4 text-pop-silhouette" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold uppercase tracking-wide text-pop-silhouette">
          {rec.title}
        </div>
        <div className="line-clamp-2 text-[11px] leading-snug text-pop-shadow">
          {rec.message}
        </div>
      </div>
    </li>
  )
}
